import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationDataModel } from '@models/notification-data.model';
import { NotificationType } from '@models/notification-type.model';
import { BasicSnackbar } from '@shared/snackbars/basic-snackbar/basic-snackbar';

export type SnackBarShowData = Omit<NotificationDataModel, 'type' | 'action'>;

const SNACKBAR_TYPE_CLASS_MAP: Record<NotificationType, string> = {
  [NotificationType.INFO]: 'info-snack-bar',
  [NotificationType.SUCCESS]: 'success-snack-bar',
  [NotificationType.WARNING]: 'warning-snack-bar',
  [NotificationType.ERROR]: 'error-snack-bar',
};

@Injectable({
  providedIn: 'root',
})
export class Notifier {
  private readonly snackBar = inject(MatSnackBar);

  readonly defaultType = NotificationType.INFO;
  readonly defaultAction = 'Close';
  readonly defaultDuration = 5000;

  show({
    data,
    type = this.defaultType,
    action = this.defaultAction,
    duration = this.defaultDuration,
    panelClass,
  }: {
    data: SnackBarShowData;
    type?: NotificationType;
    action?: string;
    duration?: number;
    panelClass?: string;
  }) {
    return this.snackBar.openFromComponent(BasicSnackbar, {
      data: {
        ...data,
        type,
        action,
      },
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: panelClass || SNACKBAR_TYPE_CLASS_MAP[type],
    });
  }

  info({
    title,
    description,
    action = this.defaultAction,
    duration = this.defaultDuration,
    panelClass,
  }: {
    title: string;
    description?: string;
    action?: string;
    duration?: number;
    panelClass?: string;
  }) {
    return this.show({
      data: { title, description },
      type: NotificationType.INFO,
      action,
      duration,
      panelClass,
    });
  }

  success({
    title,
    description,
    action = this.defaultAction,
    duration = this.defaultDuration,
    panelClass,
  }: {
    title: string;
    description?: string;
    action?: string;
    duration?: number;
    panelClass?: string;
  }) {
    return this.show({
      data: { title, description },
      type: NotificationType.SUCCESS,
      action,
      duration,
      panelClass,
    });
  }

  warning({
    title,
    description,
    action = this.defaultAction,
    duration = this.defaultDuration,
    panelClass,
  }: {
    title: string;
    description?: string;
    action?: string;
    duration?: number;
    panelClass?: string;
  }) {
    return this.show({
      data: { title, description },
      type: NotificationType.WARNING,
      action,
      duration,
      panelClass,
    });
  }

  error({
    title,
    description,
    action = this.defaultAction,
    duration = this.defaultDuration,
    panelClass,
  }: {
    title: string;
    description?: string;
    action?: string;
    duration?: number;
    panelClass?: string;
  }) {
    return this.show({
      data: { title, description },
      type: NotificationType.ERROR,
      action,
      duration,
      panelClass,
    });
  }
}
